import { type ForwardedRef, type ReactNode, forwardRef } from 'react';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import { Breakpoint } from '@treams/treams-ui';

import TalentReviewScrollPane from './TalentReviewScrollPane';

export const SIDEBAR_WIDTH = 360;

/**
 * Two panes side by side, each scrolling on its own: the page content and the
 * team sidebar. The height is the room left in the viewport, measured by
 * `useFillViewportHeight` into `--fill-height`; the page itself no longer
 * scrolls. Below the medium breakpoint the panes stack and the page scrolls
 * as usual.
 */
const Root = styled(Flex)`
	flex: 1 1 auto;
	align-items: stretch;
	height: var(--fill-height, auto);
	min-height: 0;

	@media screen and (max-width: ${Breakpoint.Medium}) {
		flex-direction: column;
		height: auto;
	}
`;

const Content = styled(TalentReviewScrollPane)`
	flex: 1 1 auto;
	width: calc(100% - ${SIDEBAR_WIDTH}px);
	min-width: 0;

	@media screen and (max-width: ${Breakpoint.Medium}) {
		width: 100%;
	}
`;

/**
 * The split page: main content on the left, a sidebar pane on the right.
 * @param props component props
 * @param props.sidebar the team sidebar
 * @param props.children the main page content
 * @param ref the root; used to measure the remaining viewport height
 */
function TalentReviewSplitLayout(
	props: {
		sidebar: ReactNode;
		children: ReactNode;
	},
	ref: ForwardedRef<HTMLDivElement>,
) {
	return (
		<Root ref={ref}>
			<Content>{props.children}</Content>
			{props.sidebar}
		</Root>
	);
}

export default forwardRef(TalentReviewSplitLayout);
